import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { jobsApi, fleetApi, API } from "@/lib/api";
import { JOB_STATUS_CONFIG, PRIORITY_CONFIG } from "@/lib/constants";
import { Lightning, SignOut, ArrowRight, Camera, MapPin, Truck, ClipboardText, CheckCircle, ArrowsClockwise, WifiSlash } from "@phosphor-icons/react";
import { toast } from "sonner";
import PhotoGallery from "@/components/photos/PhotoGallery";
import PushToggle from "@/components/pwa/PushToggle";
import QueueBadge from "@/components/pwa/QueueBadge";
import ShiftPanel from "@/components/driver/ShiftPanel";
import TodayRouteCard from "@/components/driver/TodayRouteCard";
import DriverDVIRPanel from "@/components/driver/DriverDVIRPanel";
import { enqueue, flushNow } from "@/lib/offlineQueue";

const NEXT_STATUS = {
  scheduled: "dispatched",
  dispatched: "in_progress",
  in_progress: "completed",
};

const ACTION_LABEL = {
  scheduled: "Accept",
  dispatched: "Start Job",
  in_progress: "Complete",
};

export default function DriverPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [online, setOnline] = useState(navigator.onLine);

  const load = async () => {
    try {
      const [j, a] = await Promise.all([jobsApi.list(), fleetApi.list()]);
      setJobs(j);
      setAssets(a);
    } catch (e) {
      // possibly offline; service worker may serve cached
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const onOnline = () => { setOnline(true); load(); flushNow(); };
    const onOffline = () => setOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);

  // Filter jobs that have an asset assigned and are not completed/cancelled
  const myJobs = jobs.filter((j) => j.asset_id || j.asset_name).filter((j) => !["completed", "cancelled"].includes(j.status));

  const handleAdvance = async (job) => {
    const next = NEXT_STATUS[job.status];
    if (!next) return;
    if (!navigator.onLine) {
      try {
        await enqueue({
          kind: "json",
          method: "PATCH",
          url: `${API}/jobs/${job.id}`,
          body: { status: next },
        });
        // optimistic local update so the UI reflects the new state
        setJobs((prev) => prev.map((j) => (j.id === job.id ? { ...j, status: next } : j)));
        toast.success(`Queued: ${job.job_number} → ${JOB_STATUS_CONFIG[next].label} (offline)`);
      } catch (e) {
        console.warn("[DriverPage] enqueueWrite failed for job", job.id, e);
        toast.error("Failed to queue update");
      }
      return;
    }
    try {
      await jobsApi.update(job.id, { status: next });
      toast.success(`Job ${job.job_number} → ${JOB_STATUS_CONFIG[next].label}`);
      load();
    } catch (e) {
      // network died between online check and request — fall back to queue
      try {
        await enqueue({
          kind: "json",
          method: "PATCH",
          url: `${API}/jobs/${job.id}`,
          body: { status: next },
        });
        setJobs((prev) => prev.map((j) => (j.id === job.id ? { ...j, status: next } : j)));
        toast.success(`Queued: ${job.job_number} → ${JOB_STATUS_CONFIG[next].label}`);
      } catch (_q) {
        toast.error("Failed to update");
      }
    }
  };

  return (
    <div className="min-h-screen bg-[#111111] text-white pb-24" data-testid="driver-page">
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-[#0d0d0d] border-b border-[#2a2a2a] px-4 py-3 flex items-center justify-between" data-testid="driver-header">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-[#ff8c00] flex items-center justify-center">
            <Lightning size={18} weight="fill" color="#000" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#71717a]">Driver Mode</div>
            <div className="font-heading font-black text-base tracking-tight leading-none">{user?.name || "Driver"}</div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <QueueBadge onFlushed={load} />
          {!online && (
            <span className="px-2 py-1 bg-[#ef4444]/20 border border-[#ef4444] text-[#ef4444] text-[10px] uppercase tracking-[0.2em] font-bold flex items-center gap-1" data-testid="offline-badge">
              <WifiSlash size={10} weight="bold" /> Offline
            </span>
          )}
          <PushToggle compact />
          <Link
            to="/live"
            className="px-3 py-1.5 border border-[#2a2a2a] hover:border-[#4682b4] text-[10px] uppercase tracking-[0.2em] font-bold"
            data-testid="goto-dispatcher"
          >
            Dispatcher
          </Link>
          <button
            onClick={async () => { await logout(); navigate("/login"); }}
            className="w-9 h-9 border border-[#2a2a2a] hover:border-[#ef4444] hover:text-[#ef4444] flex items-center justify-center"
            data-testid="driver-logout"
          >
            <SignOut size={14} />
          </button>
        </div>
      </header>

      <div className="px-4 py-5 max-w-2xl mx-auto">
        <ShiftPanel onShiftChange={() => load()} />
        {(() => {
          const myAsset = assets.find((a) => (a.on_shift_driver_email || "").toLowerCase() === (user?.email || "").toLowerCase());
          return (
            <DriverDVIRPanel
              assignedAssetId={myAsset?.id}
              assignedAssetName={myAsset?.name}
              driverName={user?.name || user?.email}
            />
          );
        })()}
        <TodayRouteCard />

        <div className="mb-5">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#71717a]">Today&apos;s Manifest</div>
          <h1 className="font-heading font-black text-3xl tracking-tight">{myJobs.length} Active Job{myJobs.length === 1 ? "" : "s"}</h1>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-[#71717a] uppercase tracking-wider text-xs"><ArrowsClockwise className="animate-spin" /> Loading…</div>
        ) : myJobs.length === 0 ? (
          <div className="border border-dashed border-[#2a2a2a] p-8 text-center">
            <ClipboardText size={32} className="mx-auto text-[#71717a] mb-2" />
            <div className="text-sm text-[#a1a1aa] mb-1">No active jobs assigned</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717a]">Check back later or contact dispatch</div>
          </div>
        ) : (
          <div className="space-y-4">
            {myJobs.map((job) => {
              const sCfg = JOB_STATUS_CONFIG[job.status];
              const pCfg = PRIORITY_CONFIG[job.priority];
              const isActive = activeId === job.id;
              const nextStatus = NEXT_STATUS[job.status];
              return (
                <div key={job.id} className="bg-[#0d0d0d] border border-[#2a2a2a]" data-testid={`driver-job-${job.job_number}`}>
                  <button
                    onClick={() => setActiveId(isActive ? null : job.id)}
                    className="w-full text-left p-4 hover:bg-[#1a1a1a] transition-colors"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <div className="font-mono text-xs text-[#a1a1aa]">{job.job_number}</div>
                        <div className="font-heading font-bold text-lg tracking-tight">{job.customer}</div>
                      </div>
                      <span
                        className="px-2 py-1 text-[10px] uppercase tracking-[0.2em] font-bold"
                        style={{ background: sCfg.bg, color: sCfg.color, border: `1px solid ${sCfg.color}` }}
                      >
                        {sCfg.label}
                      </span>
                    </div>
                    <div className="space-y-1.5 text-xs">
                      <div className="flex items-start gap-2 text-[#a1a1aa]">
                        <MapPin size={12} className="text-[#22c55e] mt-px shrink-0" weight="fill" />
                        <span><span className="text-[#71717a] uppercase tracking-wider text-[10px]">Pickup:</span> {job.pickup_address}</span>
                      </div>
                      <div className="flex items-start gap-2 text-[#a1a1aa]">
                        <MapPin size={12} className="text-[#ff8c00] mt-px shrink-0" weight="fill" />
                        <span><span className="text-[#71717a] uppercase tracking-wider text-[10px]">Dropoff:</span> {job.dropoff_address}</span>
                      </div>
                      <div className="flex items-center gap-3 pt-1 text-[10px] uppercase tracking-wider text-[#71717a] font-mono">
                        <span style={{ color: pCfg.color }}>● {pCfg.label}</span>
                        {job.weight_lbs > 0 && <span>{job.weight_lbs.toLocaleString()} lbs</span>}
                        {job.asset_name && <span className="text-[#4682b4]"><Truck size={10} className="inline mr-1" />{job.asset_name}</span>}
                      </div>
                    </div>
                  </button>

                  {isActive && (
                    <div className="border-t border-[#2a2a2a] p-4 space-y-4">
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-[#71717a] mb-2">Proof of Delivery</div>
                        <PhotoGallery jobId={job.id} compact />
                      </div>

                      {nextStatus && (
                        <div className="grid grid-cols-2 gap-2">
                          <a
                            href={`https://maps.google.com/?q=${encodeURIComponent(job.status === "in_progress" ? job.dropoff_address : job.pickup_address)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-center gap-2 py-3 border border-[#4682b4] text-[#4682b4] hover:bg-[#4682b4] hover:text-white text-xs uppercase tracking-[0.2em] font-bold transition-colors"
                            data-testid={`navigate-${job.job_number}`}
                          >
                            <MapPin size={14} weight="bold" /> Navigate
                          </a>
                          <button
                            onClick={() => handleAdvance(job)}
                            className="flex items-center justify-center gap-2 py-3 bg-[#ff8c00] text-black hover:bg-[#e67e00] disabled:opacity-40 text-xs uppercase tracking-[0.2em] font-bold transition-colors"
                            data-testid={`advance-${job.job_number}`}
                          >
                            <CheckCircle size={14} weight="bold" /> {ACTION_LABEL[job.status] || "Update"}{!online && " (offline)"}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
